import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Datasets, DataTitle, SubTitle, Title } from './DownloadPage.styled';
import { BACKEND_URL } from '../../../variables';
import Terms from '../../components/common/Terms';

const DownloadPage = () => {
    const [state, setState] = useState({
        datasets: [],
        taxonomies: [],
        vocabularies: [],
    });

    useEffect(() => {
        axios.get(`${BACKEND_URL}/datasets`).then((response) =>
            setState((prevState) => ({
                ...prevState,
                datasets: response.data,
            })),
        );
    }, []);

    useEffect(() => {
        axios.get(`${BACKEND_URL}/vocabularies`).then((response) =>
            setState((prevState) => ({
                ...prevState,
                vocabularies: response.data,
            })),
        );
    }, []);

    useEffect(() => {
        axios.get(`${BACKEND_URL}/taxonomies`).then((response) =>
            setState((prevState) => ({
                ...prevState,
                taxonomies: response.data,
            })),
        );
    }, []);

    const { datasets, vocabularies, taxonomies } = state;

    return (
        <Container className="container">
            <Title>Download</Title>
            <p>
                This page provides links to the linked data representation of the EPO patent data to allow the data to
                be downloaded. This data is in standardized form and can be loaded into an RDF triple store.
            </p>
            <p>There are three kinds of downloads:</p>
            <ul>
                <li>
                    <em>full dataset</em> is a complete dataset, including patent data, CPC data and vocabulary files.
                    The full dataset is large and updated weekly.
                </li>
                <li>
                    <em>cpc</em> is the latest linked data representation of the CPC taxonomy.
                </li>
                <li>
                    <em>vocabularies</em> are relatively small files containting the ontologies and vocabularies defined
                    by EPO.
                </li>
            </ul>
            <SubTitle>Full Dataset</SubTitle>
            <p>The full data contains complete and up to date patent data, CPC taxonomy and vocabulary files.</p>
            <p>
                This data is updated weekly. We provide the current and the previous weeks data identified by year and
                week number.
            </p>
            <p data-testid="fullDataset">
                The full data is provided in <a data-testid = "nTriples" href="https://www.w3.org/TR/n-triples/">N-TRIPLES</a> format. It is
                divided into multiple files to make it easier to download. Each download file is a single valid
                N-TRIPLES file compressed using GZIP. For each file, there is another file containing its SHA 512
                checksum.
            </p>
            {datasets &&
                datasets.map((dataset) => (
                    <>
                        <DataTitle key={`weekTitle${dataset.filename}`}>Full dataset for week {dataset.week}</DataTitle>
                        <Datasets key={`dataset${dataset.filename}`}>
                            {dataset.resources.map((data: any) => (
                                <span key={data.urlLink}>
                                    <a className="datasetName" key={data.fileName} href={`${BACKEND_URL}${data.urlLink}`}>
                                        {data.fileName}
                                    </a>{' '}
                                    ({data.size}
                                    {data.sizeUnit},{' '}
                                    <a className="checksum" key={data.checksumLink} href={`${BACKEND_URL}${data.checksumLink}`}>
                                        checksum
                                    </a>
                                    )
                                </span>
                            ))}
                        </Datasets>
                    </>
                ))}
           <SubTitle>CPC</SubTitle>
            <p>
                The linked data representation of the CPC taxonomy is provided as a single file in{' '}
                <a href="https://www.w3.org/TR/n-triples/">N-TRIPLES</a> format compressed using GZIP.
            </p>
            <ul>
                {taxonomies &&
                    taxonomies.map((taxonomy) => (
                        <li key={taxonomy.urlLink}>
                            <a href={`${BACKEND_URL}${taxonomy.urlLink}`}>
                                {taxonomy.fileName}
                            </a>{' '}
                            ({taxonomy.size}
                            {taxonomy.sizeUnit},{' '}
                            <a href={`${BACKEND_URL}${taxonomy.checksumLink}`}>
                                checksum
                            </a>
                            )
                        </li>
                    ))}
            </ul>
            <SubTitle>Vocabularies</SubTitle>
            <p data-testid="vocabularies">
                Vocabulary files are provided in <a data-testid="turtle" href="https://www.w3.org/TR/turtle/">TURTLE</a> format. This section
                provides the latest version of the vocabulary files which may be newer than in an initial download,
                though changes to these files are relatively infrequent.
            </p>
            <ul>
                {vocabularies &&
                    vocabularies.map((vocabulary) => (
                        <li key={vocabulary.urlLink}>
                            <a href={`${BACKEND_URL}${vocabulary.urlLink}`}>
                                {vocabulary.fileName}
                            </a>{' '}
                            ({vocabulary.size}
                            {vocabulary.sizeUnit},{' '}
                            <a href={`${BACKEND_URL}${vocabulary.checksumLink}`}>
                                checksum
                            </a>
                            )
                        </li>
                    ))}
            </ul>
            <Terms />
        </Container>
    );
};

export default DownloadPage;
